import { NavLink } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { useServiceCounts } from '../hooks/useServiceCounts';
import logo from '../images/logo.png';
import {
  FaShoppingCart, FaTimes, FaBars, FaChevronDown, FaEnvelope, FaPhone, FaFileAlt,
  FaInfoCircle, FaHandsHelping, FaTags, FaDumbbell, FaHeartbeat, FaFlask, FaBook, FaEllipsisH,
  FaSpinner, FaUtensils, FaRunning, FaMagic, FaStar, FaSeedling
} from 'react-icons/fa';

function LoginHeader() {
  const serviceCounts = useServiceCounts();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeSubDropdown, setActiveSubDropdown] = useState(null);
  const [isPWA, setIsPWA] = useState(false);
  const menuRef = useRef(null);
  const dropdownRef = useRef(null);
  const dropdownTimerRef = useRef(null);
  const servicesDropdownRef = useRef(null);

  // Detect PWA standalone mode
  useEffect(() => {
    const checkPWA = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      setIsPWA(standalone);
    };
    checkPWA();
    window.addEventListener('resize', checkPWA);
    return () => window.removeEventListener('resize', checkPWA);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
        setActiveDropdown(null);
        setActiveSubDropdown(null);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
        setActiveSubDropdown(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = (dropdownName, e) => {
    e.stopPropagation(); // Prevent event bubbling
    setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
    setActiveSubDropdown(null); // Reset sub-dropdown when main dropdown toggles
  };

  const toggleSubDropdown = (subDropdownName, e) => {
    e.stopPropagation(); // Prevent parent dropdown from closing
    setActiveSubDropdown(activeSubDropdown === subDropdownName ? null : subDropdownName);
  };

  const handleDropdownHover = (dropdownName, isEnter) => {
    if (dropdownTimerRef.current) {
      clearTimeout(dropdownTimerRef.current);
    }

    if (isEnter) {
      setActiveDropdown(dropdownName);
    } else {
      dropdownTimerRef.current = setTimeout(() => {
        setActiveDropdown(null);
        setActiveSubDropdown(null);
      }, 300);
    }
  };

  return (
    <header className={`sticky top-0 ${isPWA ? 'z-[100]' : 'z-50'}`}>
      {/* Main Header */}
      <div className={`${isPWA ? 'bg-gradient-to-r from-slate-800 via-blue-900 to-slate-900 border-b-2 border-cyan-400/40' : 'bg-gradient-to-r from-dark to-primary'} text-white ${isPWA ? 'shadow-2xl' : 'shadow-lg'} relative overflow-hidden`}>
        {isPWA && (
          <>
            {/* Top accent line */}
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-transparent via-cyan-300 via-blue-300 via-indigo-300 to-transparent"></div>
            {/* Decorative gradient overlays */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-white/15 to-transparent rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-white/15 to-transparent rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-br from-white/5 to-transparent rounded-full blur-3xl"></div>
            {/* Subtle pattern overlay */}
            <div className="absolute inset-0 opacity-5" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '24px 24px'
            }}></div>
          </>
        )}
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`flex justify-between items-center ${isPWA ? 'py-5 sm:py-6 md:py-7' : 'py-3'}`}>
            {/* Logo and Brand */}
            <div className={`flex items-center ${isPWA ? 'gap-4 sm:gap-5' : ''}`}>
              <NavLink
                to="https://youtube.com/shorts/7qhTsTAMDoM?si=cTJo43ZBnXbDF5UI"
                className="relative group/logo flex items-center"
                onClick={() => setIsMenuOpen(false)}
              >
                {isPWA && (
                  <>
                    {/* Enhanced glow effects for PWA */}
                    <div className="absolute -inset-2 bg-gradient-to-br from-white/50 via-white/30 to-transparent rounded-full blur-xl opacity-60 group-hover/logo:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute -inset-1 bg-gradient-to-br from-white/40 to-transparent rounded-full blur-lg opacity-70 group-hover/logo:opacity-90 transition-opacity duration-300"></div>
                    {/* Ring effect */}
                    <div className="absolute -inset-1.5 border-2 border-white/20 rounded-full opacity-0 group-hover/logo:opacity-100 transition-opacity duration-300"></div>
                  </>
                )}
                <div className={`absolute -inset-1 ${isPWA ? 'bg-gradient-to-br from-white/30 to-transparent blur-[10px]' : 'bg-gradient-to-br from-white/30 to-transparent blur-[10px]'} rounded-full ${isPWA ? 'opacity-80' : 'opacity-70'} group-hover/logo:opacity-100 transition-opacity duration-300`}></div>
                <div className={`relative transform ${isPWA ? 'group-hover/logo:scale-105' : 'group-hover/logo:scale-[1.02]'} transition-all duration-300 ${isPWA ? 'drop-shadow-[0_4px_12px_rgba(255,255,255,0.4)]' : 'drop-shadow-[0_2px_4px_rgba(255,255,255,0.3)]'}`}>
                  <img
                    src={logo}
                    alt="Yogvital Logo"
                    className={`${isPWA ? 'h-14 sm:h-16 md:h-18' : 'h-10'} w-auto ${isPWA ? 'drop-shadow-[0_4px_12px_rgba(255,255,255,0.5)]' : 'drop-shadow-[0_2px_4px_rgba(255,255,255,0.3)]'}`}
                  />
                </div>
              </NavLink>

              <NavLink
                to="/"
                className={`relative group/text ${isPWA ? 'ml-0' : 'ml-3'}`}
                onClick={() => setIsMenuOpen(false)}
              >
                {isPWA && (
                  <>
                    {/* Enhanced glow effects for PWA */}
                    <div className="absolute -inset-2 bg-gradient-to-br from-white/30 via-white/20 to-transparent rounded-xl blur-lg opacity-50 group-hover/text:opacity-80 transition-opacity duration-300"></div>
                    {/* Decorative accent bars - positioned symmetrically to avoid logo overlap */}
                    <div className="absolute left-2 top-1/2 -translate-y-1/2 -translate-x-2 w-1 h-8 bg-gradient-to-b from-teal-300 via-blue-300 to-indigo-300 rounded-full opacity-0 group-hover/text:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 translate-x-2 w-1 h-8 bg-gradient-to-b from-teal-300 via-blue-300 to-indigo-300 rounded-full opacity-0 group-hover/text:opacity-100 transition-opacity duration-300"></div>
                  </>
                )}
                <div className={`absolute -inset-1 ${isPWA ? 'bg-gradient-to-br from-white/20 to-transparent blur-[6px]' : 'bg-gradient-to-br from-white/20 to-transparent blur-[6px]'} rounded-lg ${isPWA ? 'opacity-60' : 'opacity-50'} group-hover/text:opacity-80 transition-opacity duration-300`}></div>
                <h1 className={`relative ${isPWA ? 'text-3xl sm:text-4xl md:text-5xl' : 'text-xl md:text-2xl'} font-bold ${isPWA ? 'group-hover/text:text-white drop-shadow-[0_2px_8px_rgba(255,255,255,0.5)] tracking-tight' : 'group-hover/text:text-accent'} transition-all duration-300 ${isPWA ? 'px-2' : ''}`}>
                  Yogvital
                </h1>
              </NavLink>
            </div>

            {/* Mobile Navigation - Hamburger Menu */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-white focus:outline-none p-2 rounded-full hover:bg-white/10 transition-colors"
                aria-label="Toggle menu"
              >
                {isMenuOpen ? (
                  <FaTimes className="w-5 h-5" />
                ) : (
                  <FaBars className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </nav>
      </div>

      {/* Secondary Black Navigation Bar */}
      <div className={`${isPWA ? 'bg-gray-100 border-b-2 border-gray-300' : 'bg-black'} ${isPWA ? 'text-gray-800' : 'text-white'} ${isPWA ? 'shadow-xl' : 'shadow-lg'} relative overflow-visible z-[90]`}>
        {isPWA && (
          <>
            {/* Top accent line */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-cyan-300 via-blue-300 via-indigo-300 to-transparent"></div>
            {/* Decorative gradient overlays */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-gray-200/20 to-transparent rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-28 h-28 bg-gradient-to-tr from-gray-200/20 to-transparent rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-gradient-to-br from-gray-200/10 to-transparent rounded-full blur-3xl"></div>
            {/* Subtle pattern overlay */}
            <div className="absolute inset-0 opacity-5" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, gray 1px, transparent 0)',
              backgroundSize: '20px 20px'
            }}></div>
          </>
        )}
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex justify-end">
            <div className={`hidden md:flex items-center ${isPWA ? 'space-x-5' : 'space-x-8'} ${isPWA ? 'py-4 sm:py-5' : 'py-3'}`}>
              <NavLink
                to="/Market"
                className={`${isPWA ? 'text-gray-800 hover:text-gray-900 font-semibold' : 'text-white hover:text-accent'} transition-colors flex items-center ${isPWA ? 'text-base md:text-lg' : 'text-sm md:text-base'} group relative`}
              >
                <div className="relative group-hover:scale-110 transition-transform">
                  <FaShoppingCart className="mr-2" />
                </div>
                <span className="font-medium">Marketplace</span>
              </NavLink>

              <div
                className="relative"
                onMouseEnter={() => handleDropdownHover('category', true)}
                onMouseLeave={() => handleDropdownHover('category', false)}
              >
                <button
                  className={`flex items-center ${isPWA ? 'text-gray-800 hover:text-gray-900 font-semibold' : 'text-white hover:text-accent'} transition-colors ${isPWA ? 'text-base md:text-lg' : 'text-sm md:text-base'} font-medium group relative`}
                  onClick={(e) => toggleDropdown('category', e)}
                >
                  <div className="relative group-hover:scale-110 transition-transform">
                    <FaTags className="mr-2" />
                  </div>
                  <span>Category</span>
                  <FaChevronDown className={`ml-1 text-xs transition-transform ${activeDropdown === 'category' ? 'transform rotate-180' : ''}`} />
                </button>
                {activeDropdown === 'category' && (
                  <div
                    className="absolute right-0 mt-2 w-64 bg-white/95 backdrop-blur-sm rounded-lg shadow-xl z-50 overflow-y-auto max-h-96 border border-white/20 p-2 scrollbar-thin scrollbar-thumb-primary scrollbar-track-white/20"
                    onMouseEnter={() => handleDropdownHover('category', true)}
                    onMouseLeave={() => handleDropdownHover('category', false)}
                  >
                    {/* Instructors Submenu */}
                    <div className="relative border-b border-white/10">
                      <button
                        className="flex items-center justify-between w-full px-4 py-2 text-dark hover:bg-primary/10 transition-all"
                        onClick={(e) => toggleSubDropdown('instructors', e)}
                      >
                        <div className="flex items-center gap-2">
                          <FaDumbbell className="h-5 w-5 text-primary" />
                          <p className="text-base font-semibold">Instructors</p>
                        </div>
                        <FaChevronDown className={`text-xs transition-transform ${activeSubDropdown === 'instructors' ? 'transform rotate-180' : ''}`} />
                      </button>
                      {activeSubDropdown === 'instructors' && (
                        <div className="bg-white/90 pl-4 border-l-2 border-primary/20">
<NavLink
                            to="/services/yoga-trainer"
                            className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                            onClick={() => {
                              setActiveDropdown(null);
                              setActiveSubDropdown(null);
                            }}
                          >
                            <div className="bg-primary/10 p-2 rounded-full">
                              <FaSpinner className="h-5 w-5 text-primary" />
                            </div>
                            <span className="font-medium">Yoga Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['yoga-trainer'] !== undefined ? `(${serviceCounts['yoga-trainer']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/zumba-trainer"
                            className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                            onClick={() => {
                              setActiveDropdown(null);
                              setActiveSubDropdown(null);
                            }}
                          >
                            <div className="bg-primary/10 p-2 rounded-full">
                              <FaRunning className="h-5 w-5 text-primary" />
                            </div>
                            <span className="font-medium">Zumba Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['zumba-trainer'] !== undefined ? `(${serviceCounts['zumba-trainer']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/reiki-trainer"
                            className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                            onClick={() => {
                              setActiveDropdown(null);
                              setActiveSubDropdown(null);
                            }}
                          >
                            <div className="bg-primary/10 p-2 rounded-full">
                              <FaMagic className="h-5 w-5 text-primary" />
                            </div>
                            <span className="font-medium">Reiki Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['reiki-trainer'] !== undefined ? `(${serviceCounts['reiki-trainer']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/home-tuition"
                            className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                            onClick={() => {
                              setActiveDropdown(null);
                              setActiveSubDropdown(null);
                            }}
                          >
                            <div className="bg-primary/10 p-2 rounded-full">
                              <FaBook className="h-5 w-5 text-primary" />
                            </div>
                            <span className="font-medium">Home Tuition <span className="text-xs opacity-75 ml-1">{serviceCounts['home-tuition'] !== undefined ? `(${serviceCounts['home-tuition']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/tarot-card-trainer"
                            className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                            onClick={() => {
                              setActiveDropdown(null);
                              setActiveSubDropdown(null);
                            }}
                          >
                            <div className="bg-primary/10 p-2 rounded-full">
                              <FaStar className="h-5 w-5 text-primary" />
                            </div>
                            <span className="font-medium">Tarot Card Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['tarot-card-trainer'] !== undefined ? `(${serviceCounts['tarot-card-trainer']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/gym-trainer"
                            className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                            onClick={() => {
                              setActiveDropdown(null);
                              setActiveSubDropdown(null);
                            }}
                          >
                            <div className="bg-primary/10 p-2 rounded-full">
                              <FaDumbbell className="h-5 w-5 text-primary" />
                            </div>
                            <span className="font-medium">Gym Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['gym-trainer'] !== undefined ? `(${serviceCounts['gym-trainer']})` : '(0)'}</span></span>
                          </NavLink>
</div>
                      )}
                    </div>

                    {/* Consultants Submenu */}
                    <div className="relative border-t border-white/10">
                      <button
                        className="flex items-center justify-between w-full px-4 py-2 text-dark hover:bg-primary/10 transition-all"
                        onClick={(e) => toggleSubDropdown('consultants', e)}
                      >
                        <div className="flex items-center gap-2">
                          <FaHandsHelping className="h-5 w-5 text-primary" />
                          <p className="text-base font-semibold">Consultants</p>
                        </div>
                        <FaChevronDown className={`text-xs transition-transform ${activeSubDropdown === 'consultants' ? 'transform rotate-180' : ''}`} />
                      </button>
                      {activeSubDropdown === 'consultants' && (
                        <div className="bg-white/90 pl-4 border-l-2 border-primary/20">
<NavLink
                            to="/services/dietitian"
                            className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                            onClick={() => {
                              setActiveDropdown(null);
                              setActiveSubDropdown(null);
                            }}
                          >
                            <div className="bg-primary/10 p-2 rounded-full">
                              <FaUtensils className="h-5 w-5 text-primary" />
                            </div>
                            <span className="font-medium">Dietitian <span className="text-xs opacity-75 ml-1">{serviceCounts['dietitian'] !== undefined ? `(${serviceCounts['dietitian']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/astrologer"
                            className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                            onClick={() => {
                              setActiveDropdown(null);
                              setActiveSubDropdown(null);
                            }}
                          >
                            <div className="bg-primary/10 p-2 rounded-full">
                              <FaStar className="h-5 w-5 text-primary" />
                            </div>
                            <span className="font-medium">Astrologer <span className="text-xs opacity-75 ml-1">{serviceCounts['astrologer'] !== undefined ? `(${serviceCounts['astrologer']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/reiki-healer"
                            className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                            onClick={() => {
                              setActiveDropdown(null);
                              setActiveSubDropdown(null);
                            }}
                          >
                            <div className="bg-primary/10 p-2 rounded-full">
                              <FaMagic className="h-5 w-5 text-primary" />
                            </div>
                            <span className="font-medium">Reiki Healer <span className="text-xs opacity-75 ml-1">{serviceCounts['reiki-healer'] !== undefined ? `(${serviceCounts['reiki-healer']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/ayurvedacharya"
                            className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                            onClick={() => {
                              setActiveDropdown(null);
                              setActiveSubDropdown(null);
                            }}
                          >
                            <div className="bg-primary/10 p-2 rounded-full">
                              <FaHeartbeat className="h-5 w-5 text-primary" />
                            </div>
                            <span className="font-medium">Ayurvedacharya <span className="text-xs opacity-75 ml-1">{serviceCounts['ayurvedacharya'] !== undefined ? `(${serviceCounts['ayurvedacharya']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/numerologer"
                            className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                            onClick={() => {
                              setActiveDropdown(null);
                              setActiveSubDropdown(null);
                            }}
                          >
                            <div className="bg-primary/10 p-2 rounded-full">
                              <FaSeedling className="h-5 w-5 text-primary" />
                            </div>
                            <span className="font-medium">Numerologer <span className="text-xs opacity-75 ml-1">{serviceCounts['numerologer'] !== undefined ? `(${serviceCounts['numerologer']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/tarot-card-reader"
                            className="flex items-center px-4 py-2 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                            onClick={() => {
                              setActiveDropdown(null);
                              setActiveSubDropdown(null);
                            }}
                          >
                            <div className="bg-primary/10 p-2 rounded-full">
                              <FaStar className="h-5 w-5 text-primary" />
                            </div>
                            <span className="font-medium">Tarot Card Reader <span className="text-xs opacity-75 ml-1">{serviceCounts['tarot-card-reader'] !== undefined ? `(${serviceCounts['tarot-card-reader']})` : '(0)'}</span></span>
                          </NavLink>
</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Services Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => handleDropdownHover('services', true)}
                onMouseLeave={() => handleDropdownHover('services', false)}
                ref={servicesDropdownRef}
              >
                <button
                  className={`flex items-center ${isPWA ? 'text-gray-800 hover:text-gray-900 font-semibold' : 'text-white hover:text-accent'} transition-colors ${isPWA ? 'text-base md:text-lg' : 'text-sm md:text-base'} font-medium group relative`}
                  onClick={(e) => toggleDropdown('services', e)}
                >
                  <div className="relative group-hover:scale-110 transition-transform">
                    <FaHandsHelping className="mr-2" />
                  </div>
                  <span>Services</span>
                  <FaChevronDown className={`ml-1 text-xs transition-transform ${activeDropdown === 'services' ? 'transform rotate-180' : ''}`} />
                </button>
                {activeDropdown === 'services' && (
                  <div
                    className="absolute right-0 mt-2 w-64 bg-white/95 backdrop-blur-sm rounded-lg shadow-xl z-50 overflow-hidden border border-white/20"
                    onMouseEnter={() => handleDropdownHover('services', true)}
                    onMouseLeave={() => handleDropdownHover('services', false)}
                  >
                    <div className="max-h-[360px] overflow-y-auto overflow-x-hidden">
                      {/* Fitness Submenu */}
                      <div className="relative border-b border-white/10">
                        <button
                          className="flex items-center justify-between w-full px-4 py-3 text-dark hover:bg-primary/10 transition-all font-medium"
                          onClick={(e) => toggleSubDropdown('services-fitness', e)}
                        >
                          <div className="flex items-center gap-2">
                            <FaDumbbell className="h-5 w-5 text-primary" />
                            <p className="text-base font-semibold">Fitness</p>
                          </div>
                          <FaChevronDown className={`text-xs transition-transform ${activeSubDropdown === 'services-fitness' ? 'transform rotate-180' : ''}`} />
                        </button>
                        {activeSubDropdown === 'services-fitness' && (
                          <div className="bg-white/90 pl-4 border-l-2 border-primary/20">
                            <NavLink
                              to="/services/yoga-trainer"
                              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:pl-5 hover:shadow-md"
                              onClick={() => setActiveDropdown(null)}
                            >
                              <div className="bg-primary/10 p-2 rounded-full">
                                <FaSpinner className="h-5 w-5 text-primary" />
                              </div>
                              <span className="font-medium">Yoga Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['yoga-trainer'] !== undefined ? `(${serviceCounts['yoga-trainer']})` : '(0)'}</span></span>
                            </NavLink>
                            <NavLink
                              to="/services/dietitian"
                              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:pl-5 hover:shadow-md"
                              onClick={() => setActiveDropdown(null)}
                            >
                              <div className="bg-primary/10 p-2 rounded-full">
                                <FaUtensils className="h-5 w-5 text-primary" />
                              </div>
                              <span className="font-medium">Dietitian <span className="text-xs opacity-75 ml-1">{serviceCounts['dietitian'] !== undefined ? `(${serviceCounts['dietitian']})` : '(0)'}</span></span>
                            </NavLink>
                            <NavLink
                              to="/services/gym-trainer"
                              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:pl-5 hover:shadow-md"
                              onClick={() => setActiveDropdown(null)}
                            >
                              <div className="bg-primary/10 p-2 rounded-full">
                                <FaDumbbell className="h-5 w-5 text-primary" />
                              </div>
                              <span className="font-medium">Gym Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['gym-trainer'] !== undefined ? `(${serviceCounts['gym-trainer']})` : '(0)'}</span></span>
                            </NavLink>
                            <NavLink
                              to="/services/zumba-trainer"
                              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:pl-5 hover:shadow-md"
                              onClick={() => setActiveDropdown(null)}
                            >
                              <div className="bg-primary/10 p-2 rounded-full">
                                <FaRunning className="h-5 w-5 text-primary" />
                              </div>
                              <span className="font-medium">Zumba Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['zumba-trainer'] !== undefined ? `(${serviceCounts['zumba-trainer']})` : '(0)'}</span></span>
                            </NavLink>
                          </div>
                        )}
                      </div>

                      {/* Wellness Submenu */}
                      <div className="relative border-b border-white/10">
                        <button
                          className="flex items-center justify-between w-full px-4 py-3 text-dark hover:bg-primary/10 transition-all font-medium"
                          onClick={(e) => toggleSubDropdown('services-wellness', e)}
                        >
                          <div className="flex items-center gap-2">
                            <FaMagic className="h-5 w-5 text-primary" />
                            <p className="text-base font-semibold">Wellness</p>
                          </div>
                          <FaChevronDown className={`text-xs transition-transform ${activeSubDropdown === 'services-wellness' ? 'transform rotate-180' : ''}`} />
                        </button>
                        {activeSubDropdown === 'services-wellness' && (
                          <div className="bg-white/90 pl-4 border-l-2 border-primary/20">
                            <NavLink
                              to="/services/reiki-healer"
                              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:pl-5 hover:shadow-md"
                              onClick={() => setActiveDropdown(null)}
                            >
                              <div className="bg-primary/10 p-2 rounded-full">
                                <FaMagic className="h-5 w-5 text-primary" />
                              </div>
                              <span className="font-medium">Reiki Healer <span className="text-xs opacity-75 ml-1">{serviceCounts['reiki-healer'] !== undefined ? `(${serviceCounts['reiki-healer']})` : '(0)'}</span></span>
                            </NavLink>
                            <NavLink
                              to="/services/astrologer"
                              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:pl-5 hover:shadow-md"
                              onClick={() => setActiveDropdown(null)}
                            >
                              <div className="bg-primary/10 p-2 rounded-full">
                                <FaStar className="h-5 w-5 text-primary" />
                              </div>
                              <span className="font-medium">Astrologer <span className="text-xs opacity-75 ml-1">{serviceCounts['astrologer'] !== undefined ? `(${serviceCounts['astrologer']})` : '(0)'}</span></span>
                            </NavLink>
                            <NavLink
                              to="/services/reiki-trainer"
                              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:pl-5 hover:shadow-md"
                              onClick={() => setActiveDropdown(null)}
                            >
                              <div className="bg-primary/10 p-2 rounded-full">
                                <FaMagic className="h-5 w-5 text-primary" />
                              </div>
                              <span className="font-medium">Reiki Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['reiki-trainer'] !== undefined ? `(${serviceCounts['reiki-trainer']})` : '(0)'}</span></span>
                            </NavLink>
                            <NavLink
                              to="/services/numerologer"
                              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:pl-5 hover:shadow-md"
                              onClick={() => setActiveDropdown(null)}
                            >
                              <div className="bg-primary/10 p-2 rounded-full">
                                <FaSeedling className="h-5 w-5 text-primary" />
                              </div>
                              <span className="font-medium">Numerologer <span className="text-xs opacity-75 ml-1">{serviceCounts['numerologer'] !== undefined ? `(${serviceCounts['numerologer']})` : '(0)'}</span></span>
                            </NavLink>
                            <NavLink
                              to="/services/tarot-card-reader"
                              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:pl-5 hover:shadow-md"
                              onClick={() => setActiveDropdown(null)}
                            >
                              <div className="bg-primary/10 p-2 rounded-full">
                                <FaStar className="h-5 w-5 text-primary" />
                              </div>
                              <span className="font-medium">Tarot Card Reader <span className="text-xs opacity-75 ml-1">{serviceCounts['tarot-card-reader'] !== undefined ? `(${serviceCounts['tarot-card-reader']})` : '(0)'}</span></span>
                            </NavLink>
                            <NavLink
                              to="/services/tarot-card-trainer"
                              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:pl-5 hover:shadow-md"
                              onClick={() => setActiveDropdown(null)}
                            >
                              <div className="bg-primary/10 p-2 rounded-full">
                                <FaStar className="h-5 w-5 text-primary" />
                              </div>
                              <span className="font-medium">Tarot Card Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['tarot-card-trainer'] !== undefined ? `(${serviceCounts['tarot-card-trainer']})` : '(0)'}</span></span>
                            </NavLink>
                          </div>
                        )}
                      </div>

                      {/* Medical Submenu */}
                      <div className="relative border-b border-white/10">
                        <button
                          className="flex items-center justify-between w-full px-4 py-3 text-dark hover:bg-primary/10 transition-all font-medium"
                          onClick={(e) => toggleSubDropdown('services-medical', e)}
                        >
                          <div className="flex items-center gap-2">
                            <FaFlask className="h-5 w-5 text-primary" />
                            <p className="text-base font-semibold">Medical</p>
                          </div>
                          <FaChevronDown className={`text-xs transition-transform ${activeSubDropdown === 'services-medical' ? 'transform rotate-180' : ''}`} />
                        </button>
                        {activeSubDropdown === 'services-medical' && (
                          <div className="bg-white/90 pl-4 border-l-2 border-primary/20">
                            <NavLink
                              to="/services/ayurvedacharya"
                              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:pl-5 hover:shadow-md"
                              onClick={() => setActiveDropdown(null)}
                            >
                              <div className="bg-primary/10 p-2 rounded-full">
                                <FaHeartbeat className="h-5 w-5 text-primary" />
                              </div>
                              <span className="font-medium">Ayurvedacharya <span className="text-xs opacity-75 ml-1">{serviceCounts['ayurvedacharya'] !== undefined ? `(${serviceCounts['ayurvedacharya']})` : '(0)'}</span></span>
                            </NavLink>
                          </div>
                        )}
                      </div>

                      {/* Knowledge Submenu */}
                      <div className="relative">
                        <button
                          className="flex items-center justify-between w-full px-4 py-3 text-dark hover:bg-primary/10 transition-all font-medium"
                          onClick={(e) => toggleSubDropdown('services-knowledge', e)}
                        >
                          <div className="flex items-center gap-2">
                            <FaBook className="h-5 w-5 text-primary" />
                            <p className="text-base font-semibold">Knowledge</p>
                          </div>
                          <FaChevronDown className={`text-xs transition-transform ${activeSubDropdown === 'services-knowledge' ? 'transform rotate-180' : ''}`} />
                        </button>
                        {activeSubDropdown === 'services-knowledge' && (
                          <div className="bg-white/90 pl-4 border-l-2 border-primary/20">
                            <NavLink
                              to="/services/home-tuition"
                              className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:pl-5 hover:shadow-md"
                              onClick={() => setActiveDropdown(null)}
                            >
                              <div className="bg-primary/10 p-2 rounded-full">
                                <FaBook className="h-5 w-5 text-primary" />
                              </div>
                              <span className="font-medium">Home Tuition <span className="text-xs opacity-75 ml-1">{serviceCounts['home-tuition'] !== undefined ? `(${serviceCounts['home-tuition']})` : '(0)'}</span></span>
                            </NavLink>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <NavLink
                to="/about"
                className={`${isPWA ? 'text-gray-800 hover:text-gray-900 font-semibold' : 'text-white hover:text-accent'} transition-colors flex items-center ${isPWA ? 'text-base md:text-lg' : 'text-sm md:text-base'} group relative`}
              >
                <div className="relative group-hover:scale-110 transition-transform">
                  <FaInfoCircle className="mr-2" />
                </div>
                <span className="font-medium">About Us</span>
              </NavLink>

              <div
                className="relative"
                onMouseEnter={() => handleDropdownHover('contact', true)}
                onMouseLeave={() => handleDropdownHover('contact', false)}
              >
                <button
                  className={`flex items-center ${isPWA ? 'text-gray-800 hover:text-gray-900 font-semibold' : 'text-white hover:text-accent'} transition-colors ${isPWA ? 'text-base md:text-lg' : 'text-sm md:text-base'} font-medium group relative`}
                  onClick={(e) => toggleDropdown('contact', e)}
                >
                  <div className="relative group-hover:scale-110 transition-transform">
                    <FaEnvelope className="mr-2" />
                  </div>
                  <span>Contact Us</span>
                </button>
                {activeDropdown === 'contact' && (
                  <div
                    className="absolute right-0 mt-2 w-56 bg-white/95 backdrop-blur-sm rounded-lg shadow-xl z-50 overflow-y-auto max-h-96 border border-white/20 p-2 scrollbar-thin scrollbar-thumb-primary scrollbar-track-white/20"
                    onMouseEnter={() => handleDropdownHover('contact', true)}
                    onMouseLeave={() => handleDropdownHover('contact', false)}
                  >
                    <a
                      href="mailto:contact@yogvital.com"
                      className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 border-b border-white/10 hover:translate-x-1 hover:shadow-md"
                    >
                      <div className="bg-primary/10 p-2 rounded-full">
                        <FaEnvelope className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">Email Us</p>
                        <p className="text-xs text-gray-500">contact@yogvital.com</p>
                      </div>
                    </a>
                    <a
                      href="tel:+918923162316"
                      className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 border-b border-white/10 hover:translate-x-1 hover:shadow-md"
                    >
                      <div className="bg-primary/10 p-2 rounded-full">
                        <FaPhone className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">Call Us</p>
                        <p className="text-xs text-gray-500">+91 89231 62316</p>
                      </div>
                    </a>
                    <NavLink
                      to="/contact-form"
                      className="flex items-center px-4 py-3 hover:bg-primary/10 text-dark transition-all gap-3 hover:translate-x-1 hover:shadow-md"
                      onClick={() => setActiveDropdown(null)}
                    >
                      <div className="bg-primary/10 p-2 rounded-full">
                        <FaFileAlt className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">Contact Form</p>
                        <p className="text-xs text-gray-500">Send us a message</p>
                      </div>
                    </NavLink>
                  </div>
                )}
              </div>
            </div>
          </div>
        </nav>
      </div >

      {/* Mobile Menu Content - Enhanced for PWA */}
      {
        isMenuOpen && (
          <div
            ref={menuRef}
            className={`md:hidden ${isPWA ? 'bg-gradient-to-br from-white to-teal-50/30 border-2 border-teal-200/60 shadow-2xl' : 'bg-dark/95 backdrop-blur-sm border border-white/20'} rounded-xl sm:rounded-2xl mx-2 mb-2 overflow-y-auto max-h-[85vh] transition-all duration-300 p-4 sm:p-5 relative overflow-hidden ${isPWA ? 'scrollbar-thin scrollbar-thumb-teal-300 scrollbar-track-teal-100/20' : 'scrollbar-thin scrollbar-thumb-primary scrollbar-track-dark/20'}`}
          >
            {/* PWA Decorative Elements */}
            {isPWA && (
              <>
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 via-blue-400 to-indigo-400"></div>
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-teal-100/30 to-blue-100/20 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-br from-blue-100/30 to-indigo-100/20 rounded-full blur-3xl"></div>
              </>
            )}

            <div className={`space-y-2 sm:space-y-3 p-2 relative z-10`}>
              <NavLink
                to="/Market"
                className={`flex items-center ${isPWA ? 'px-4 sm:px-5 py-3.5 sm:py-4 bg-gradient-to-br from-white to-teal-50/50 border-2 border-teal-200/60 hover:border-teal-300/80 hover:from-teal-50 hover:to-blue-50 text-teal-800 font-semibold' : 'px-4 py-3 hover:bg-white/10 text-white'} rounded-xl sm:rounded-2xl transition-all duration-300 hover:translate-x-1 hover:shadow-lg group`}
                onClick={() => setIsMenuOpen(false)}
              >
                <div className={`${isPWA ? 'bg-gradient-to-br from-teal-100 to-blue-100 p-3 rounded-xl shadow-md ring-2 ring-teal-200/50' : 'bg-accent/10 p-2 rounded-full'} mr-3 group-hover:scale-110 transition-transform`}>
                  <FaShoppingCart className={`h-5 w-5 ${isPWA ? 'text-teal-600' : 'text-accent'}`} />
                </div>
                <span className={`${isPWA ? 'text-base sm:text-lg' : ''}`}>Marketplace</span>
              </NavLink>

              <div className="relative">
                <button
                  className={`flex items-center justify-between w-full ${isPWA ? 'px-4 sm:px-5 py-3.5 sm:py-4 bg-gradient-to-br from-white to-teal-50/50 border-2 border-teal-200/60 hover:border-teal-300/80 hover:from-teal-50 hover:to-blue-50 text-teal-800 font-semibold' : 'px-4 py-3 hover:bg-white/10 text-white'} rounded-xl sm:rounded-2xl transition-all duration-300 group`}
                  onClick={(e) => toggleDropdown('category-mobile', e)}
                >
                  <div className="flex items-center">
                    <div className={`${isPWA ? 'bg-gradient-to-br from-teal-100 to-blue-100 p-3 rounded-xl shadow-md ring-2 ring-teal-200/50' : 'bg-accent/10 p-2 rounded-full'} mr-3 group-hover:scale-110 transition-transform`}>
                      <FaTags className={`h-5 w-5 ${isPWA ? 'text-teal-600' : 'text-accent'}`} />
                    </div>
                    <span className={`${isPWA ? 'text-base sm:text-lg' : ''}`}>Category</span>
                  </div>
                  <FaChevronDown className={`${isPWA ? 'text-teal-600' : 'text-white'} text-xs transition-transform ${activeDropdown === 'category-mobile' ? 'transform rotate-180' : ''}`} />
                </button>
                {activeDropdown === 'category-mobile' && (
                  <div className={`ml-4 mt-2 space-y-2 ${isPWA ? 'bg-gradient-to-br from-teal-50/80 to-blue-50/60 border-2 border-teal-200/50 rounded-xl p-3' : 'bg-dark/80 rounded-lg p-2'} overflow-y-auto max-h-80 ${isPWA ? 'scrollbar-thin scrollbar-thumb-teal-300 scrollbar-track-teal-100/20' : 'scrollbar-thin scrollbar-thumb-primary scrollbar-track-dark/20'} animate-fade-in`}>
                    {/* Instructors Submenu */}
                    <div className="relative border-b border-primary/10">
                      <button
                        className={`flex items-center justify-between w-full px-4 py-2.5 ${isPWA ? 'bg-gradient-to-br from-teal-100/80 to-blue-100/60 border border-teal-200/50 text-teal-800 hover:from-teal-100 hover:to-blue-100' : 'bg-primary/10 text-dark hover:bg-primary/20'} rounded-lg transition-all duration-300`}
                        onClick={(e) => toggleSubDropdown('instructors-mobile', e)}
                      >
                        <div className="flex items-center gap-2">
                          <FaDumbbell className={`h-5 w-5 ${isPWA ? 'text-teal-600' : 'text-primary'}`} />
                          <p className={`text-base font-semibold ${isPWA ? 'text-teal-800' : ''}`}>Instructors</p>
                        </div>
                        <FaChevronDown className={`${isPWA ? 'text-teal-600' : 'text-dark'} text-xs transition-transform ${activeSubDropdown === 'instructors-mobile' ? 'transform rotate-180' : ''}`} />
                      </button>
                      {activeSubDropdown === 'instructors-mobile' && (
                        <div className={`${isPWA ? 'bg-gradient-to-br from-white to-teal-50/40 pl-4 mt-2 space-y-1.5 border-l-2 border-teal-200/50' : 'bg-dark/90 pl-4 border-l-2 border-primary/20'} rounded-lg`}>
<NavLink
                            to="/services/yoga-trainer"
                            className={`flex items-center px-4 py-2.5 ${isPWA ? 'hover:bg-gradient-to-r hover:from-teal-50 hover:to-blue-50 text-teal-800 border border-teal-100/50' : 'hover:bg-white/10 text-white'} rounded-lg transition-all duration-300 hover:translate-x-1 hover:shadow-md group`}
                            onClick={() => {
                              setIsMenuOpen(false);
                              setActiveDropdown(null);
                              setActiveSubDropdown(null);
                            }}
                          >
                            <div className={`${isPWA ? 'bg-gradient-to-br from-teal-100 to-blue-100 p-2 rounded-lg shadow-sm ring-1 ring-teal-200/50' : 'bg-primary/10 p-2 rounded-full'} mr-2 group-hover:scale-110 transition-transform`}>
                              <FaSpinner className={`h-5 w-5 ${isPWA ? 'text-teal-600' : 'text-primary'}`} />
                            </div>
                            <span className={`font-medium ${isPWA ? 'text-sm sm:text-base' : ''}`}>Yoga Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['yoga-trainer'] !== undefined ? `(${serviceCounts['yoga-trainer']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/zumba-trainer"
                            className={`flex items-center px-4 py-2.5 ${isPWA ? 'hover:bg-gradient-to-r hover:from-teal-50 hover:to-blue-50 text-teal-800 border border-teal-100/50' : 'hover:bg-white/10 text-white'} rounded-lg transition-all duration-300 hover:translate-x-1 hover:shadow-md group`}
                            onClick={() => {
                              setIsMenuOpen(false);
                              setActiveDropdown(null);
                              setActiveSubDropdown(null);
                            }}
                          >
                            <div className={`${isPWA ? 'bg-gradient-to-br from-teal-100 to-blue-100 p-2 rounded-lg shadow-sm ring-1 ring-teal-200/50' : 'bg-primary/10 p-2 rounded-full'} mr-2 group-hover:scale-110 transition-transform`}>
                              <FaRunning className={`h-5 w-5 ${isPWA ? 'text-teal-600' : 'text-primary'}`} />
                            </div>
                            <span className={`font-medium ${isPWA ? 'text-sm sm:text-base' : ''}`}>Zumba Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['zumba-trainer'] !== undefined ? `(${serviceCounts['zumba-trainer']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/reiki-trainer"
                            className={`flex items-center px-4 py-2.5 ${isPWA ? 'hover:bg-gradient-to-r hover:from-teal-50 hover:to-blue-50 text-teal-800 border border-teal-100/50' : 'hover:bg-white/10 text-white'} rounded-lg transition-all duration-300 hover:translate-x-1 hover:shadow-md group`}
                            onClick={() => {
                              setIsMenuOpen(false);
                              setActiveDropdown(null);
                              setActiveSubDropdown(null);
                            }}
                          >
                            <div className={`${isPWA ? 'bg-gradient-to-br from-teal-100 to-blue-100 p-2 rounded-lg shadow-sm ring-1 ring-teal-200/50' : 'bg-primary/10 p-2 rounded-full'} mr-2 group-hover:scale-110 transition-transform`}>
                              <FaMagic className={`h-5 w-5 ${isPWA ? 'text-teal-600' : 'text-primary'}`} />
                            </div>
                            <span className={`font-medium ${isPWA ? 'text-sm sm:text-base' : ''}`}>Reiki Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['reiki-trainer'] !== undefined ? `(${serviceCounts['reiki-trainer']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/home-tuition"
                            className={`flex items-center px-4 py-2.5 ${isPWA ? 'hover:bg-gradient-to-r hover:from-teal-50 hover:to-blue-50 text-teal-800 border border-teal-100/50' : 'hover:bg-white/10 text-white'} rounded-lg transition-all duration-300 hover:translate-x-1 hover:shadow-md group`}
                            onClick={() => {
                              setIsMenuOpen(false);
                              setActiveDropdown(null);
                              setActiveSubDropdown(null);
                            }}
                          >
                            <div className={`${isPWA ? 'bg-gradient-to-br from-teal-100 to-blue-100 p-2 rounded-lg shadow-sm ring-1 ring-teal-200/50' : 'bg-primary/10 p-2 rounded-full'} mr-2 group-hover:scale-110 transition-transform`}>
                              <FaBook className={`h-5 w-5 ${isPWA ? 'text-teal-600' : 'text-primary'}`} />
                            </div>
                            <span className={`font-medium ${isPWA ? 'text-sm sm:text-base' : ''}`}>Home Tuition <span className="text-xs opacity-75 ml-1">{serviceCounts['home-tuition'] !== undefined ? `(${serviceCounts['home-tuition']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/tarot-card-reader"
                            className={`flex items-center px-4 py-2.5 ${isPWA ? 'hover:bg-gradient-to-r hover:from-teal-50 hover:to-blue-50 text-teal-800 border border-teal-100/50' : 'hover:bg-white/10 text-white'} rounded-lg transition-all duration-300 hover:translate-x-1 hover:shadow-md group`}
                            onClick={() => {
                              setIsMenuOpen(false);
                              setActiveDropdown(null);
                              setActiveSubDropdown(null);
                            }}
                          >
                            <div className={`${isPWA ? 'bg-gradient-to-br from-teal-100 to-blue-100 p-2 rounded-lg shadow-sm ring-1 ring-teal-200/50' : 'bg-primary/10 p-2 rounded-full'} mr-2 group-hover:scale-110 transition-transform`}>
                              <FaStar className={`h-5 w-5 ${isPWA ? 'text-teal-600' : 'text-primary'}`} />
                            </div>
                            <span className={`font-medium ${isPWA ? 'text-sm sm:text-base' : ''}`}>Tarot Card Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['tarot-card-reader'] !== undefined ? `(${serviceCounts['tarot-card-reader']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/gym-trainer"
                            className={`flex items-center px-4 py-2.5 ${isPWA ? 'hover:bg-gradient-to-r hover:from-teal-50 hover:to-blue-50 text-teal-800 border border-teal-100/50' : 'hover:bg-white/10 text-white'} rounded-lg transition-all duration-300 hover:translate-x-1 hover:shadow-md group`}
                            onClick={() => {
                              setIsMenuOpen(false);
                              setActiveDropdown(null);
                              setActiveSubDropdown(null);
                            }}
                          >
                            <div className={`${isPWA ? 'bg-gradient-to-br from-teal-100 to-blue-100 p-2 rounded-lg shadow-sm ring-1 ring-teal-200/50' : 'bg-primary/10 p-2 rounded-full'} mr-2 group-hover:scale-110 transition-transform`}>
                              <FaDumbbell className={`h-5 w-5 ${isPWA ? 'text-teal-600' : 'text-primary'}`} />
                            </div>
                            <span className={`font-medium ${isPWA ? 'text-sm sm:text-base' : ''}`}>Gym Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['gym-trainer'] !== undefined ? `(${serviceCounts['gym-trainer']})` : '(0)'}</span></span>
                          </NavLink>
</div>
                      )}
                    </div>

                    {/* Consultants Submenu */}
                    <div className="relative">
                      <button
                        className={`flex items-center justify-between w-full px-4 py-2.5 ${isPWA ? 'bg-gradient-to-br from-teal-100/80 to-blue-100/60 border border-teal-200/50 text-teal-800 hover:from-teal-100 hover:to-blue-100' : 'bg-primary/10 text-dark hover:bg-primary/20'} rounded-lg transition-all duration-300`}
                        onClick={(e) => toggleSubDropdown('consultants-mobile', e)}
                      >
                        <div className="flex items-center gap-2">
                          <FaHandsHelping className={`h-5 w-5 ${isPWA ? 'text-teal-600' : 'text-primary'}`} />
                          <p className={`text-base font-semibold ${isPWA ? 'text-teal-800' : ''}`}>Consultants</p>
                        </div>
                        <FaChevronDown className={`${isPWA ? 'text-teal-600' : 'text-dark'} text-xs transition-transform ${activeSubDropdown === 'consultants-mobile' ? 'transform rotate-180' : ''}`} />
                      </button>
                      {activeSubDropdown === 'consultants-mobile' && (
                        <div className={`${isPWA ? 'bg-gradient-to-br from-white to-teal-50/40 pl-4 mt-2 space-y-1.5 border-l-2 border-teal-200/50' : 'bg-dark/90 pl-4 border-l-2 border-primary/20'} rounded-lg`}>
<NavLink
                            to="/services/dietitian"
                            className={`flex items-center px-4 py-2.5 ${isPWA ? 'hover:bg-gradient-to-r hover:from-teal-50 hover:to-blue-50 text-teal-800 border border-teal-100/50' : 'hover:bg-white/10 text-white'} rounded-lg transition-all duration-300 hover:translate-x-1 hover:shadow-md group`}
                            onClick={() => {
                              setIsMenuOpen(false);
                              setActiveDropdown(null);
                              setActiveSubDropdown(null);
                            }}
                          >
                            <div className={`${isPWA ? 'bg-gradient-to-br from-teal-100 to-blue-100 p-2 rounded-lg shadow-sm ring-1 ring-teal-200/50' : 'bg-primary/10 p-2 rounded-full'} mr-2 group-hover:scale-110 transition-transform`}>
                              <FaUtensils className={`h-5 w-5 ${isPWA ? 'text-teal-600' : 'text-primary'}`} />
                            </div>
                            <span className={`font-medium ${isPWA ? 'text-sm sm:text-base' : ''}`}>Dietitian <span className="text-xs opacity-75 ml-1">{serviceCounts['dietitian'] !== undefined ? `(${serviceCounts['dietitian']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/astrologer"
                            className={`flex items-center px-4 py-2.5 ${isPWA ? 'hover:bg-gradient-to-r hover:from-teal-50 hover:to-blue-50 text-teal-800 border border-teal-100/50' : 'hover:bg-white/10 text-white'} rounded-lg transition-all duration-300 hover:translate-x-1 hover:shadow-md group`}
                            onClick={() => {
                              setIsMenuOpen(false);
                              setActiveDropdown(null);
                              setActiveSubDropdown(null);
                            }}
                          >
                            <div className={`${isPWA ? 'bg-gradient-to-br from-teal-100 to-blue-100 p-2 rounded-lg shadow-sm ring-1 ring-teal-200/50' : 'bg-primary/10 p-2 rounded-full'} mr-2 group-hover:scale-110 transition-transform`}>
                              <FaStar className={`h-5 w-5 ${isPWA ? 'text-teal-600' : 'text-primary'}`} />
                            </div>
                            <span className={`font-medium ${isPWA ? 'text-sm sm:text-base' : ''}`}>Astrologer <span className="text-xs opacity-75 ml-1">{serviceCounts['astrologer'] !== undefined ? `(${serviceCounts['astrologer']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/reiki-healer"
                            className={`flex items-center px-4 py-2.5 ${isPWA ? 'hover:bg-gradient-to-r hover:from-teal-50 hover:to-blue-50 text-teal-800 border border-teal-100/50' : 'hover:bg-white/10 text-white'} rounded-lg transition-all duration-300 hover:translate-x-1 hover:shadow-md group`}
                            onClick={() => {
                              setIsMenuOpen(false);
                              setActiveDropdown(null);
                              setActiveSubDropdown(null);
                            }}
                          >
                            <div className={`${isPWA ? 'bg-gradient-to-br from-teal-100 to-blue-100 p-2 rounded-lg shadow-sm ring-1 ring-teal-200/50' : 'bg-primary/10 p-2 rounded-full'} mr-2 group-hover:scale-110 transition-transform`}>
                              <FaMagic className={`h-5 w-5 ${isPWA ? 'text-teal-600' : 'text-primary'}`} />
                            </div>
                            <span className={`font-medium ${isPWA ? 'text-sm sm:text-base' : ''}`}>Reiki Healer <span className="text-xs opacity-75 ml-1">{serviceCounts['reiki-healer'] !== undefined ? `(${serviceCounts['reiki-healer']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/ayurvedacharya"
                            className={`flex items-center px-4 py-2.5 ${isPWA ? 'hover:bg-gradient-to-r hover:from-teal-50 hover:to-blue-50 text-teal-800 border border-teal-100/50' : 'hover:bg-white/10 text-white'} rounded-lg transition-all duration-300 hover:translate-x-1 hover:shadow-md group`}
                            onClick={() => {
                              setIsMenuOpen(false);
                              setActiveDropdown(null);
                              setActiveSubDropdown(null);
                            }}
                          >
                            <div className={`${isPWA ? 'bg-gradient-to-br from-teal-100 to-blue-100 p-2 rounded-lg shadow-sm ring-1 ring-teal-200/50' : 'bg-primary/10 p-2 rounded-full'} mr-2 group-hover:scale-110 transition-transform`}>
                              <FaHeartbeat className={`h-5 w-5 ${isPWA ? 'text-teal-600' : 'text-primary'}`} />
                            </div>
                            <span className={`font-medium ${isPWA ? 'text-sm sm:text-base' : ''}`}>Ayurvedacharya <span className="text-xs opacity-75 ml-1">{serviceCounts['ayurvedacharya'] !== undefined ? `(${serviceCounts['ayurvedacharya']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/numerologer"
                            className={`flex items-center px-4 py-2.5 ${isPWA ? 'hover:bg-gradient-to-r hover:from-teal-50 hover:to-blue-50 text-teal-800 border border-teal-100/50' : 'hover:bg-white/10 text-white'} rounded-lg transition-all duration-300 hover:translate-x-1 hover:shadow-md group`}
                            onClick={() => {
                              setIsMenuOpen(false);
                              setActiveDropdown(null);
                              setActiveSubDropdown(null);
                            }}
                          >
                            <div className={`${isPWA ? 'bg-gradient-to-br from-teal-100 to-blue-100 p-2 rounded-lg shadow-sm ring-1 ring-teal-200/50' : 'bg-primary/10 p-2 rounded-full'} mr-2 group-hover:scale-110 transition-transform`}>
                              <FaSeedling className={`h-5 w-5 ${isPWA ? 'text-teal-600' : 'text-primary'}`} />
                            </div>
                            <span className={`font-medium ${isPWA ? 'text-sm sm:text-base' : ''}`}>Numerologer <span className="text-xs opacity-75 ml-1">{serviceCounts['numerologer'] !== undefined ? `(${serviceCounts['numerologer']})` : '(0)'}</span></span>
                          </NavLink>
<NavLink
                            to="/services/tarot-card-reader"
                            className={`flex items-center px-4 py-2.5 ${isPWA ? 'hover:bg-gradient-to-r hover:from-teal-50 hover:to-blue-50 text-teal-800 border border-teal-100/50' : 'hover:bg-white/10 text-white'} rounded-lg transition-all duration-300 hover:translate-x-1 hover:shadow-md group`}
                            onClick={() => {
                              setIsMenuOpen(false);
                              setActiveDropdown(null);
                              setActiveSubDropdown(null);
                            }}
                          >
                            <div className={`${isPWA ? 'bg-gradient-to-br from-teal-100 to-blue-100 p-2 rounded-lg shadow-sm ring-1 ring-teal-200/50' : 'bg-primary/10 p-2 rounded-full'} mr-2 group-hover:scale-110 transition-transform`}>
                              <FaStar className={`h-5 w-5 ${isPWA ? 'text-teal-600' : 'text-primary'}`} />
                            </div>
                            <span className={`font-medium ${isPWA ? 'text-sm sm:text-base' : ''}`}>Tarot Card Reader <span className="text-xs opacity-75 ml-1">{serviceCounts['tarot-card-reader'] !== undefined ? `(${serviceCounts['tarot-card-reader']})` : '(0)'}</span></span>
                          </NavLink>
</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="relative">
                <button
                  className={`flex items-center justify-between w-full ${isPWA ? 'px-4 sm:px-5 py-3.5 sm:py-4 bg-gradient-to-br from-white to-blue-50/50 border-2 border-blue-200/60 hover:border-blue-300/80 hover:from-blue-50 hover:to-indigo-50 text-blue-800 font-semibold' : 'px-4 py-3 hover:bg-white/10 text-white'} rounded-xl sm:rounded-2xl transition-all duration-300 group`}
                  onClick={(e) => toggleDropdown('services-mobile', e)}
                >
                  <div className="flex items-center">
                    <div className={`${isPWA ? 'bg-gradient-to-br from-blue-100 to-indigo-100 p-3 rounded-xl shadow-md ring-2 ring-blue-200/50' : 'bg-accent/10 p-2 rounded-full'} mr-3 group-hover:scale-110 transition-transform`}>
                      <FaHandsHelping className={`h-5 w-5 ${isPWA ? 'text-blue-600' : 'text-accent'}`} />
                    </div>
                    <span className={`${isPWA ? 'text-base sm:text-lg' : ''}`}>Services</span>
                  </div>
                  <FaChevronDown className={`${isPWA ? 'text-blue-600' : 'text-white'} text-xs transition-transform ${activeDropdown === 'services-mobile' ? 'transform rotate-180' : ''}`} />
                </button>
                {activeDropdown === 'services-mobile' && (
                  <div className={`ml-6 mt-2 space-y-1.5 ${isPWA ? 'bg-gradient-to-br from-blue-50/80 to-indigo-50/60 border-2 border-blue-200/50 rounded-xl p-3' : 'bg-dark/80 rounded-lg'} overflow-y-auto max-h-80 p-2 scrollbar-thin scrollbar-thumb-primary scrollbar-track-white/20 animate-fade-in`}>
                    {/* Fitness Submenu */}
                    <div className="relative border-b border-primary/10">
                      <button
                        className="flex items-center justify-between w-full px-4 py-2 hover:bg-primary/10 text-white transition-all rounded-lg"
                        onClick={(e) => toggleSubDropdown('services-mobile-fitness', e)}
                      >
                        <div className="flex items-center gap-2">
                          <FaDumbbell className="h-5 w-5 text-primary" />
                          <p className="text-base font-semibold">Fitness</p>
                        </div>
                        <FaChevronDown className={`text-xs transition-transform ${activeSubDropdown === 'services-mobile-fitness' ? 'transform rotate-180' : ''}`} />
                      </button>
                      {activeSubDropdown === 'services-mobile-fitness' && (
                        <div className="bg-white/10 pl-4 rounded-lg border-l-2 border-primary/20">
                          <NavLink
                            to="/services/yoga-trainer"
                            className={`flex items-center px-4 py-2.5 ${isPWA ? 'hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 text-blue-800 border border-blue-100/50' : 'hover:bg-white/10 text-white'} rounded-lg transition-all duration-300 hover:translate-x-1 hover:shadow-md group`}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <div className={`${isPWA ? 'bg-gradient-to-br from-blue-100 to-indigo-100 p-2 rounded-lg shadow-sm ring-1 ring-blue-200/50' : 'bg-primary/10 p-2 rounded-full'} mr-2 group-hover:scale-110 transition-transform`}>
                              <FaSpinner className={`h-5 w-5 ${isPWA ? 'text-blue-600' : 'text-primary'}`} />
                            </div>
                            <span className={`font-medium ${isPWA ? 'text-sm sm:text-base' : ''}`}>Yoga Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['yoga-trainer'] !== undefined ? `(${serviceCounts['yoga-trainer']})` : '(0)'}</span></span>
                          </NavLink>
                          <NavLink
                            to="/services/dietitian"
                            className={`flex items-center px-4 py-2.5 ${isPWA ? 'hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 text-blue-800 border border-blue-100/50' : 'hover:bg-white/10 text-white'} rounded-lg transition-all duration-300 hover:translate-x-1 hover:shadow-md group`}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <div className={`${isPWA ? 'bg-gradient-to-br from-blue-100 to-indigo-100 p-2 rounded-lg shadow-sm ring-1 ring-blue-200/50' : 'bg-primary/10 p-2 rounded-full'} mr-2 group-hover:scale-110 transition-transform`}>
                              <FaUtensils className={`h-5 w-5 ${isPWA ? 'text-blue-600' : 'text-primary'}`} />
                            </div>
                            <span className={`font-medium ${isPWA ? 'text-sm sm:text-base' : ''}`}>Dietitian <span className="text-xs opacity-75 ml-1">{serviceCounts['dietitian'] !== undefined ? `(${serviceCounts['dietitian']})` : '(0)'}</span></span>
                          </NavLink>
                          <NavLink
                            to="/services/gym-trainer"
                            className={`flex items-center px-4 py-2.5 ${isPWA ? 'hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 text-blue-800 border border-blue-100/50' : 'hover:bg-white/10 text-white'} rounded-lg transition-all duration-300 hover:translate-x-1 hover:shadow-md group`}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <div className={`${isPWA ? 'bg-gradient-to-br from-blue-100 to-indigo-100 p-2 rounded-lg shadow-sm ring-1 ring-blue-200/50' : 'bg-primary/10 p-2 rounded-full'} mr-2 group-hover:scale-110 transition-transform`}>
                              <FaDumbbell className={`h-5 w-5 ${isPWA ? 'text-blue-600' : 'text-primary'}`} />
                            </div>
                            <span className={`font-medium ${isPWA ? 'text-sm sm:text-base' : ''}`}>Gym Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['gym-trainer'] !== undefined ? `(${serviceCounts['gym-trainer']})` : '(0)'}</span></span>
                          </NavLink>
                          <NavLink
                            to="/services/zumba-trainer"
                            className={`flex items-center px-4 py-2.5 ${isPWA ? 'hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 text-blue-800 border border-blue-100/50' : 'hover:bg-white/10 text-white'} rounded-lg transition-all duration-300 hover:translate-x-1 hover:shadow-md group`}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <div className={`${isPWA ? 'bg-gradient-to-br from-blue-100 to-indigo-100 p-2 rounded-lg shadow-sm ring-1 ring-blue-200/50' : 'bg-primary/10 p-2 rounded-full'} mr-2 group-hover:scale-110 transition-transform`}>
                              <FaRunning className={`h-5 w-5 ${isPWA ? 'text-blue-600' : 'text-primary'}`} />
                            </div>
                            <span className={`font-medium ${isPWA ? 'text-sm sm:text-base' : ''}`}>Zumba Trainer <span className="text-xs opacity-75 ml-1">{serviceCounts['zumba-trainer'] !== undefined ? `(${serviceCounts['zumba-trainer']})` : '(0)'}</span></span>
                          </NavLink>
                        </div>
                      )}
                    </div>

                    {/* Wellness Submenu */}
                    <div className="relative border-b border-primary/10">
                      <button
                        className="flex items-center justify-between w-full px-4 py-2 hover:bg-primary/10 text-white transition-all rounded-lg"
                        onClick={(e) => toggleSubDropdown('services-mobile-wellness', e)}
                      >
                        <div className="flex items-center gap-2">
                          <FaMagic className="h-5 w-5 text-primary" />
                          <p className="text-base font-semibold">Wellness</p>
                        </div>
                        <FaChevronDown className={`text-xs transition-transform ${activeSubDropdown === 'services-mobile-wellness' ? 'transform rotate-180' : ''}`} />
                      </button>
                      {activeSubDropdown === 'services-mobile-wellness' && (
                        <div className="bg-white/10 pl-4 rounded-lg border-l-2 border-primary/20">
                          <NavLink
                            to="/services/reiki-healer"
                            className={`flex items-center px-4 py-2.5 ${isPWA ? 'hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 text-blue-800 border border-blue-100/50' : 'hover:bg-white/10 text-white'} rounded-lg transition-all duration-300 hover:translate-x-1 hover:shadow-md group`}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <div className={`${isPWA ? 'bg-gradient-to-br from-blue-100 to-indigo-100 p-2 rounded-lg shadow-sm ring-1 ring-blue-200/50' : 'bg-primary/10 p-2 rounded-full'} mr-2 group-hover:scale-110 transition-transform`}>
                              <FaMagic className={`h-5 w-5 ${isPWA ? 'text-blue-600' : 'text-primary'}`} />
                            </div>
                            <span className={`font-medium ${isPWA ? 'text-sm sm:text-base' : ''}`}>Reiki Healer <span className="text-xs opacity-75 ml-1">{serviceCounts['reiki-healer'] !== undefined ? `(${serviceCounts['reiki-healer']})` : '(0)'}</span></span>
                          </NavLink>
                          <NavLink
                            to="/services/astrologer"
                            className={`flex items-center px-4 py-2.5 ${isPWA ? 'hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 text-blue-800 border border-blue-100/50' : 'hover:bg-white/10 text-white'} rounded-lg transition-all duration-300 hover:translate-x-1 hover:shadow-md group`}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <div className={`${isPWA ? 'bg-gradient-to-br from-blue-100 to-indigo-100 p-2 rounded-lg shadow-sm ring-1 ring-blue-200/50' : 'bg-primary/10 p-2 rounded-full'} mr-2 group-hover:scale-110 transition-transform`}>
                              <FaStar className={`h-5 w-5 ${isPWA ? 'text-blue-600' : 'text-primary'}`} />
                            </div>
                            <span className={`font-medium ${isPWA ? 'text-sm sm:text-base' : ''}`}>Astrologer <span className="text-xs opacity-75 ml-1">{serviceCounts['astrologer'] !== undefined ? `(${serviceCounts['astrologer']})` : '(0)'}</span></span>
                          </NavLink>
                          <NavLink
                            to="/services/numerologer"
                            className={`flex items-center px-4 py-2.5 ${isPWA ? 'hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 text-blue-800 border border-blue-100/50' : 'hover:bg-white/10 text-white'} rounded-lg transition-all duration-300 hover:translate-x-1 hover:shadow-md group`}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <div className={`${isPWA ? 'bg-gradient-to-br from-blue-100 to-indigo-100 p-2 rounded-lg shadow-sm ring-1 ring-blue-200/50' : 'bg-primary/10 p-2 rounded-full'} mr-2 group-hover:scale-110 transition-transform`}>
                              <FaSeedling className={`h-5 w-5 ${isPWA ? 'text-blue-600' : 'text-primary'}`} />
                            </div>
                            <span className={`font-medium ${isPWA ? 'text-sm sm:text-base' : ''}`}>Numerologer <span className="text-xs opacity-75 ml-1">{serviceCounts['numerologer'] !== undefined ? `(${serviceCounts['numerologer']})` : '(0)'}</span></span>
                          </NavLink>
                          <NavLink
                            to="/services/tarot-card-reader"
                            className={`flex items-center px-4 py-2.5 ${isPWA ? 'hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 text-blue-800 border border-blue-100/50' : 'hover:bg-white/10 text-white'} rounded-lg transition-all duration-300 hover:translate-x-1 hover:shadow-md group`}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <div className={`${isPWA ? 'bg-gradient-to-br from-blue-100 to-indigo-100 p-2 rounded-lg shadow-sm ring-1 ring-blue-200/50' : 'bg-primary/10 p-2 rounded-full'} mr-2 group-hover:scale-110 transition-transform`}>
                              <FaStar className={`h-5 w-5 ${isPWA ? 'text-blue-600' : 'text-primary'}`} />
                            </div>
                            <span className={`font-medium ${isPWA ? 'text-sm sm:text-base' : ''}`}>Tarot Card Reader <span className="text-xs opacity-75 ml-1">{serviceCounts['tarot-card-reader'] !== undefined ? `(${serviceCounts['tarot-card-reader']})` : '(0)'}</span></span>
                          </NavLink>
                        </div>
                      )}
                    </div>

                    {/* Medical Submenu */}
                    <div className="relative border-b border-primary/10">
                      <button
                        className="flex items-center justify-between w-full px-4 py-2 hover:bg-primary/10 text-white transition-all rounded-lg"
                        onClick={(e) => toggleSubDropdown('services-mobile-medical', e)}
                      >
                        <div className="flex items-center gap-2">
                          <FaFlask className="h-5 w-5 text-primary" />
                          <p className="text-base font-semibold">Medical</p>
                        </div>
                        <FaChevronDown className={`text-xs transition-transform ${activeSubDropdown === 'services-mobile-medical' ? 'transform rotate-180' : ''}`} />
                      </button>
                      {activeSubDropdown === 'services-mobile-medical' && (
                        <div className="bg-white/10 pl-4 rounded-lg border-l-2 border-primary/20">
                          <NavLink
                            to="/services/ayurvedacharya"
                            className={`flex items-center px-4 py-2.5 ${isPWA ? 'hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 text-blue-800 border border-blue-100/50' : 'hover:bg-white/10 text-white'} rounded-lg transition-all duration-300 hover:translate-x-1 hover:shadow-md group`}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <div className={`${isPWA ? 'bg-gradient-to-br from-blue-100 to-indigo-100 p-2 rounded-lg shadow-sm ring-1 ring-blue-200/50' : 'bg-primary/10 p-2 rounded-full'} mr-2 group-hover:scale-110 transition-transform`}>
                              <FaHeartbeat className={`h-5 w-5 ${isPWA ? 'text-blue-600' : 'text-primary'}`} />
                            </div>
                            <span className={`font-medium ${isPWA ? 'text-sm sm:text-base' : ''}`}>Ayurvedacharya <span className="text-xs opacity-75 ml-1">{serviceCounts['ayurvedacharya'] !== undefined ? `(${serviceCounts['ayurvedacharya']})` : '(0)'}</span></span>
                          </NavLink>
                        </div>
                      )}
                    </div>

                    {/* Knowledge Submenu */}
                    <div className="relative">
                      <button
                        className="flex items-center justify-between w-full px-4 py-2 hover:bg-primary/10 text-white transition-all rounded-lg"
                        onClick={(e) => toggleSubDropdown('services-mobile-knowledge', e)}
                      >
                        <div className="flex items-center gap-2">
                          <FaBook className="h-5 w-5 text-primary" />
                          <p className="text-base font-semibold">Knowledge</p>
                        </div>
                        <FaChevronDown className={`text-xs transition-transform ${activeSubDropdown === 'services-mobile-knowledge' ? 'transform rotate-180' : ''}`} />
                      </button>
                      {activeSubDropdown === 'services-mobile-knowledge' && (
                        <div className="bg-white/10 pl-4 rounded-lg border-l-2 border-primary/20">
                          <NavLink
                            to="/services/home-tuition"
                            className={`flex items-center px-4 py-2.5 ${isPWA ? 'hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 text-blue-800 border border-blue-100/50' : 'hover:bg-white/10 text-white'} rounded-lg transition-all duration-300 hover:translate-x-1 hover:shadow-md group`}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <div className={`${isPWA ? 'bg-gradient-to-br from-blue-100 to-indigo-100 p-2 rounded-lg shadow-sm ring-1 ring-blue-200/50' : 'bg-primary/10 p-2 rounded-full'} mr-2 group-hover:scale-110 transition-transform`}>
                              <FaBook className={`h-5 w-5 ${isPWA ? 'text-blue-600' : 'text-primary'}`} />
                            </div>
                            <span className={`font-medium ${isPWA ? 'text-sm sm:text-base' : ''}`}>Home Tuition <span className="text-xs opacity-75 ml-1">{serviceCounts['home-tuition'] !== undefined ? `(${serviceCounts['home-tuition']})` : '(0)'}</span></span>
                          </NavLink>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <NavLink
                to="/about"
                className={`flex items-center ${isPWA ? 'px-4 sm:px-5 py-3.5 sm:py-4 bg-gradient-to-br from-white to-indigo-50/50 border-2 border-indigo-200/60 hover:border-indigo-300/80 hover:from-indigo-50 hover:to-purple-50 text-indigo-800 font-semibold' : 'px-4 py-3 hover:bg-white/10 text-white'} rounded-xl sm:rounded-2xl transition-all duration-300 hover:translate-x-1 hover:shadow-lg group`}
                onClick={() => setIsMenuOpen(false)}
              >
                <div className={`${isPWA ? 'bg-gradient-to-br from-indigo-100 to-purple-100 p-3 rounded-xl shadow-md ring-2 ring-indigo-200/50' : 'bg-accent/10 p-2 rounded-full'} mr-3 group-hover:scale-110 transition-transform`}>
                  <FaInfoCircle className={`h-5 w-5 ${isPWA ? 'text-indigo-600' : 'text-accent'}`} />
                </div>
                <span className={`${isPWA ? 'text-base sm:text-lg' : ''}`}>About Us</span>
              </NavLink>

              <div className="relative">
                <button
                  className={`flex items-center justify-between w-full ${isPWA ? 'px-4 sm:px-5 py-3.5 sm:py-4 bg-gradient-to-br from-white to-cyan-50/50 border-2 border-cyan-200/60 hover:border-cyan-300/80 hover:from-cyan-50 hover:to-teal-50 text-cyan-800 font-semibold' : 'px-4 py-3 hover:bg-white/10 text-white'} rounded-xl sm:rounded-2xl transition-all duration-300 group`}
                  onClick={(e) => toggleDropdown('contact-mobile', e)}
                >
                  <div className="flex items-center">
                    <div className={`${isPWA ? 'bg-gradient-to-br from-cyan-100 to-teal-100 p-3 rounded-xl shadow-md ring-2 ring-cyan-200/50' : 'bg-accent/10 p-2 rounded-full'} mr-3 group-hover:scale-110 transition-transform`}>
                      <FaEnvelope className={`h-5 w-5 ${isPWA ? 'text-cyan-600' : 'text-accent'}`} />
                    </div>
                    <span className={`${isPWA ? 'text-base sm:text-lg' : ''}`}>Contact Us</span>
                  </div>
                  <FaChevronDown className={`${isPWA ? 'text-cyan-600' : 'text-white'} text-xs transition-transform ${activeDropdown === 'contact-mobile' ? 'transform rotate-180' : ''}`} />
                </button>
                {activeDropdown === 'contact-mobile' && (
                  <div className={`ml-6 mt-2 space-y-1.5 ${isPWA ? 'bg-gradient-to-br from-cyan-50/80 to-teal-50/60 border-2 border-cyan-200/50 rounded-xl p-3' : 'bg-dark/80 rounded-lg'} overflow-hidden animate-fade-in`}>
                    <a
                      href="mailto:contact@yogvital.com"
                      className={`flex items-center px-4 py-2.5 ${isPWA ? 'hover:bg-gradient-to-r hover:from-cyan-50 hover:to-teal-50 text-cyan-800 border border-cyan-100/50' : 'hover:bg-white/10 text-white'} rounded-lg transition-all duration-300 hover:translate-x-1 hover:shadow-md group`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <div className={`${isPWA ? 'bg-gradient-to-br from-cyan-100 to-teal-100 p-2 rounded-lg shadow-sm ring-1 ring-cyan-200/50' : 'bg-primary/10 p-2 rounded-full'} mr-2 group-hover:scale-110 transition-transform`}>
                        <FaEnvelope className={`h-5 w-5 ${isPWA ? 'text-cyan-600' : 'text-primary'}`} />
                      </div>
                      <span className={`font-medium ${isPWA ? 'text-sm sm:text-base' : ''}`}>Email Us</span>
                    </a>
                    <a
                      href="tel:+918923162316"
                      className={`flex items-center px-4 py-2.5 ${isPWA ? 'hover:bg-gradient-to-r hover:from-cyan-50 hover:to-teal-50 text-cyan-800 border border-cyan-100/50' : 'hover:bg-white/10 text-white'} rounded-lg transition-all duration-300 hover:translate-x-1 hover:shadow-md group`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <div className={`${isPWA ? 'bg-gradient-to-br from-cyan-100 to-teal-100 p-2 rounded-lg shadow-sm ring-1 ring-cyan-200/50' : 'bg-primary/10 p-2 rounded-full'} mr-2 group-hover:scale-110 transition-transform`}>
                        <FaPhone className={`h-5 w-5 ${isPWA ? 'text-cyan-600' : 'text-primary'}`} />
                      </div>
                      <span className={`font-medium ${isPWA ? 'text-sm sm:text-base' : ''}`}>Call Us</span>
                    </a>
                    <NavLink
                      to="/contact-form"
                      className={`flex items-center px-4 py-2.5 ${isPWA ? 'hover:bg-gradient-to-r hover:from-cyan-50 hover:to-teal-50 text-cyan-800 border border-cyan-100/50' : 'hover:bg-white/10 text-white'} rounded-lg transition-all duration-300 hover:translate-x-1 hover:shadow-md group`}
                      onClick={() => {
                        setIsMenuOpen(false);
                        setActiveDropdown(null);
                      }}
                    >
                      <div className={`${isPWA ? 'bg-gradient-to-br from-cyan-100 to-teal-100 p-2 rounded-lg shadow-sm ring-1 ring-cyan-200/50' : 'bg-primary/10 p-2 rounded-full'} mr-2 group-hover:scale-110 transition-transform`}>
                        <FaFileAlt className={`h-5 w-5 ${isPWA ? 'text-cyan-600' : 'text-primary'}`} />
                      </div>
                      <span className={`font-medium ${isPWA ? 'text-sm sm:text-base' : ''}`}>Contact Form</span>
                    </NavLink>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      }

    </header >
  );
}

export default LoginHeader;